import React, { useEffect, useState, useMemo } from 'react';
import api from '../services/api';
import { DollarSign, Download, Plus, Trash2, AlertCircle } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import './Users.css';
import './SalaryManagement.css';

const MySalary = () => {
  const { success, error } = useNotifications();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [payroll, setPayroll] = useState(null);
  const [user, setUser] = useState(null);
  const [taskBonus, setTaskBonus] = useState(0);
  
  // Salary advance states
  const [advances, setAdvances] = useState([]);
  const [showAdvanceForm, setShowAdvanceForm] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [advanceMonth, setAdvanceMonth] = useState(new Date().toISOString().slice(0, 7));
  const [advanceReason, setAdvanceReason] = useState('');
  const [submittingAdvance, setSubmittingAdvance] = useState(false);
  
  // Modal states
  const [rejectModal, setRejectModal] = useState(null); // { title, reason }
  const [cancelModal, setCancelModal] = useState(null); // advance id to cancel
  
  // Fetch user's salary advances
  const fetchAdvances = async () => {
    try {
      const data = await api.get('/salary/advances/me');
      setAdvances(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading advances:', err);
    }
  };
  
  // Helper function to convert decimal hours to HH:MM format
  const formatHoursMinutes = (decimalHours) => {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.get('/salary/me');
        setRecords(Array.isArray(data) ? data : []);
        
        // Load current user info
        const userInfo = await api.get('/users/me');
        setUser(userInfo);
        
        // Load salary advances
        await fetchAdvances();
      } catch (error) {
        console.error('Error loading salary records:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);
  
  useEffect(() => {
    const loadPayroll = async () => {
      if (!selectedMonth || !user) return;
      
      // Reset task bonus before loading to avoid showing stale data
      setTaskBonus(0);
      
      try {
        const data = await api.get(`/salary/user/${user.id}/monthly/${selectedMonth}`);
        setPayroll(data);

        const [yearStr, monthStr] = selectedMonth.split('-');
        const monthNum = Number(monthStr);
        // Fetch project rewards
        const projectRewards = await api.get(`/projects/rewards/my-monthly?year=${yearStr}&month=${monthNum}`).catch(() => null);
        const totalProjectReward = projectRewards?.totalReward || 0;
        setTaskBonus(Number(totalProjectReward) || 0);
      } catch (err) {
        console.error('Error loading payroll:', err);
        setPayroll(null);
        setTaskBonus(0);
      }
    };
    loadPayroll();
  }, [selectedMonth, user]);
  
  // The amounts are calculated by the server (monthly salary, counted hours, extra hour price)
  const calculatedSalary = useMemo(() => {
    if (!payroll) return { total: 0, regular: 0, overtime: 0, net: 0, advance: 0 };
    const totalAdvances = payroll.totals?.totalAdvances || 0;
    
    const regularPay = Number(payroll.totals?.regularPay || 0);
    const overtimePay = Number(payroll.totals?.overtimePay || 0);
    const gross = regularPay + overtimePay + taskBonus;
    const net = payroll.totals?.netSalary || gross - totalAdvances;
    
    return {
      total: gross,
      regular: regularPay,
      overtime: overtimePay,
      taskBonus,
      net,
      advance: totalAdvances,
    };
  }, [payroll, taskBonus]);
  
  const [exporting, setExporting] = useState(false);

  const exportPayslip = async () => {
    if (!payroll || !user || !selectedMonth) return;
    setExporting(true);
    try {
      const blob = await api.downloadBlob(`/salary/me/monthly/${selectedMonth}/export`);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fiche_de_paie_${user.username || user.id}_${selectedMonth}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      success('Fiche de paie exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      error('Failed to export fiche de paie.');
    } finally {
      setExporting(false);
    }
  };

  // Submit salary advance request
  const submitAdvanceRequest = async (e) => {
    e.preventDefault();
    if (!advanceAmount || Number(advanceAmount) <= 0) {
      error('Please enter a valid amount');
      return;
    }
    if (advanceReason && advanceReason.length > 500) {
      error('Reason must be 500 characters or less');
      return;
    }
    
    setSubmittingAdvance(true);
    try {
      await api.post('/salary/advances', {
        amount: Number(advanceAmount),
        month: advanceMonth,
        reason: advanceReason || undefined,
      });
      success('Salary advance request submitted successfully!');
      setShowAdvanceForm(false);
      setAdvanceAmount('');
      setAdvanceReason('');
      await fetchAdvances();
    } catch (err) {
      error(err.message || 'Failed to submit advance request');
    } finally {
      setSubmittingAdvance(false);
    }
  };

  // Show cancel confirmation modal
  const confirmCancelAdvance = (id) => {
    setCancelModal(id);
  };

  // Execute cancel after modal confirmation
  const executeCancelAdvance = async () => {
    if (!cancelModal) return;
    try {
      await api.delete(`/salary/advances/${cancelModal}`);
      success('Advance request cancelled');
      setCancelModal(null);
      await fetchAdvances();
    } catch (err) {
      error('Failed to cancel advance request');
    }
  };

  // Show reject reason popup
  const showRejectReason = (advance) => {
    setRejectModal({
      title: `Rejected Advance - ${Number(advance.amount).toFixed(2)} TND`,
      reason: advance.rejectionReason || 'No reason provided'
    });
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#f59e0b', text: 'Pending' },
      approved: { bg: '#10b981', text: 'Approved' },
      rejected: { bg: '#ef4444', text: 'Rejected' },
      deducted: { bg: '#6366f1', text: 'Deducted' },
    };
    return styles[status] || { bg: '#6b7280', text: status };
  };

  if (loading) return <div className="page-loading">Loading salary records...</div>;

  return (
    <div className="users-page">
      <div className="page-header">
        <h1><DollarSign size={24} /> My Salary</h1>
      </div>
      
      <div className="salary-toolbar">
        <div className="form-group" style={{ minWidth: 180 }}>
          <label>Month</label>
          <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
        </div>
        {payroll && (
          <div className="form-group" style={{ alignSelf: 'end' }}>
            <button className="btn btn-primary" onClick={exportPayslip} disabled={exporting}>
              <Download size={14} /> {exporting ? 'Exporting...' : 'Export fiche de paie'}
            </button>
          </div>
        )}
        <div className="form-group" style={{ alignSelf: 'end' }}>
          <button className="btn btn-secondary" onClick={() => setShowAdvanceForm(!showAdvanceForm)}>
            <Plus size={14} /> Request Advance
          </button>
        </div>
      </div>

      {/* Salary Advance Form */}
      {showAdvanceForm && (
        <div className="salary-summary-card" style={{ marginBottom: 20 }}>
          <h3>Request Salary Advance</h3>
          <form onSubmit={submitAdvanceRequest}>
            <div className="form-row">
              <div className="form-group">
                <label>Amount (TND)</label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={advanceAmount}
                  onChange={(e) => setAdvanceAmount(e.target.value)}
                  placeholder="Enter amount"
                  required
                />
              </div>
              <div className="form-group">
                <label>Deduction Month</label>
                <input
                  type="month"
                  value={advanceMonth}
                  onChange={(e) => setAdvanceMonth(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Reason (Optional)</label>
              <textarea
                value={advanceReason}
                onChange={(e) => setAdvanceReason(e.target.value)}
                placeholder="Explain why you need this advance..."
                rows={2}
                maxLength={500}
              />
            </div>
            <div className="form-row" style={{ marginTop: 10 }}>
              <button type="submit" className="btn btn-primary" disabled={submittingAdvance}>
                {submittingAdvance ? 'Submitting...' : 'Submit Request'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowAdvanceForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {payroll && user ? (
        <>
          <div className="salary-summary-card">
            <div className="salary-metrics">
              <div className="metric-box"><span>Normal Hours</span><strong>{payroll.totals?.regularHours ?? 0}h</strong></div>
              <div className="metric-box"><span>Extra Hours (× {payroll.overtimeMultiplier ?? 1.25})</span><strong>{payroll.totals?.overtimeHours ?? 0}h</strong></div>
              <div className="metric-box"><span>Remote Hours</span><strong>{formatHoursMinutes(payroll.totals?.remoteHours ?? 0)}</strong></div>
              <div className="metric-box salary-breakdown">
                <span>Gross Salary</span>
                <div className="salary-details">
                  <div className="salary-total">{calculatedSalary.total.toFixed(2)} {payroll?.currency || 'TND'}</div>
                  <div className="salary-components">
                    <small>Normal hours: {calculatedSalary.regular.toFixed(2)}</small>
                    <small>Extra hours: {calculatedSalary.overtime.toFixed(2)}</small>
                    <small>Project bonus: {calculatedSalary.taskBonus.toFixed(2)}</small>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Show advance deduction and net salary */}
            {calculatedSalary.advance > 0 ? (
              <div style={{ marginTop: 15, padding: '10px 15px', background: '#e3ef99', borderRadius: 8, border: '1px solid #ddb12c' }}>
                <AlertCircle size={16} style={{ marginRight: 8, verticalAlign: 'middle', color: '#ddb12c' }} />
                <strong>Advance Deduction:</strong> {calculatedSalary.advance.toFixed(2)} {payroll?.currency || 'TND'} 
                <span style={{ marginLeft: 15, padding: '4px 10px', background: '#10b981', color: 'white', borderRadius: 4 }}>
                  <strong>Net to Pay: {calculatedSalary.net.toFixed(2)} {payroll?.currency || 'TND'}</strong>
                </span>
              </div>
            ) : (
              <div style={{ marginTop: 15, padding: '10px 15px', background: '#d1fae5', borderRadius: 8, border: '1px solid #10b981' }}>
                <strong>Net to Pay: {calculatedSalary.net.toFixed(2)} {payroll?.currency || 'TND'}</strong>
              </div>
            )}
          </div>

          {/* Salary Advances List */}
          {advances.length > 0 && (
            <div className="salary-summary-card" style={{ marginTop: 20 }}>
              <h3>My Advance Requests</h3>
              <div className="users-table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Amount</th>
                      <th>Month</th>
                      <th>Status</th>
                      <th>Requested</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {advances.map((adv) => {
                      const status = getStatusBadge(adv.status);
                      return (
                        <tr key={adv.id}>
                          <td>{Number(adv.amount).toFixed(2)} TND</td>
                          <td>{adv.month}</td>
                          <td>
                            <span
                              onClick={() => adv.status === 'rejected' && showRejectReason(adv)}
                              style={{
                                padding: '4px 8px',
                                borderRadius: 4,
                                background: status.bg,
                                color: 'white',
                                fontSize: 12,
                                fontWeight: 500,
                                cursor: adv.status === 'rejected' ? 'pointer' : 'default',
                                textDecoration: adv.status === 'rejected' ? 'underline' : 'none',
                              }}
                              title={adv.status === 'rejected' ? 'Click to see rejection reason' : ''}
                            >
                              {status.text}
                            </span>
                          </td>
                          <td>{new Date(adv.createdAt).toLocaleDateString()}</td>
                          <td>
                            {adv.status === 'pending' && (
                              <button className="btn btn-danger" onClick={() => confirmCancelAdvance(adv.id)} title="Cancel request">
                                <Trash2 size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          <div className="salary-days-list">
            {(payroll.days || []).map((day) => {
              const regularMinutes = day.regularMinutes || 0;
              const overtimeMinutes = day.overtimeMinutes || 0;
              // The bar shows the counted hours: normal, then extra
              const safeTotal = Math.max(regularMinutes + overtimeMinutes, 1);
              const regularPct = (regularMinutes / safeTotal) * 100;
              const overtimePct = (overtimeMinutes / safeTotal) * 100;

              return (
                <div key={day.date} className="salary-day-row">
                  <div className="salary-day-date">{day.date}</div>
                  <div className="salary-day-bar-wrap">
                    <div className="salary-day-bar">
                      <div className="salary-day-regular" style={{ width: `${regularPct}%` }} />
                      <div className="salary-day-overtime" style={{ width: `${overtimePct}%` }} />
                    </div>
                  </div>
                  <div className="salary-day-values">
                    <span className="regular-hours">Normal: {day.regularHours || 0}h</span>
                    <span className="extra-hours">Extra: {day.overtimeHours || 0}h</span>
                    <span className="remote-hours">
                      {day.type === 'leave'
                        ? 'Approved leave'
                        : `Worked ${formatHoursMinutes(day.totalHours || 0)}${day.remoteMinutes ? ` (remote ${formatHoursMinutes(day.remoteHours || 0)})` : ''}`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="users-table-container">
          <div className="empty-row" style={{ padding: 20 }}>
            {selectedMonth ? 'No payroll data for selected month.' : 'Select a month to view your salary details.'}
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {rejectModal && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onClick={() => setRejectModal(null)}>
          <div className="salary-summary-card" style={{ maxWidth: 400, width: '90%', margin: 0 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#ef4444', marginBottom: 15 }}><AlertCircle size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />{rejectModal.title}</h3>
            <div style={{ padding: 15, background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca', marginBottom: 20 }}>
              <strong>Rejection Reason:</strong>
              <p style={{ margin: '8px 0 0 0', color: '#374151' }}>{rejectModal.reason}</p>
            </div>
            <div className="form-row" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {cancelModal && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onClick={() => setCancelModal(null)}>
          <div className="salary-summary-card" style={{ maxWidth: 380, width: '90%', margin: 0 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 15 }}>Cancel Advance Request?</h3>
            <p style={{ marginBottom: 20, color: '#6b7280' }}>
              Are you sure you want to cancel this advance request? This action cannot be undone.
            </p>
            <div className="form-row" style={{ justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setCancelModal(null)}>Keep Request</button>
              <button className="btn btn-danger" onClick={executeCancelAdvance}>Yes, Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySalary;
